import React from 'react';
import ReactDOM from 'react-dom/client';
import { PrivyProvider } from '@privy-io/react-auth';
import { SmartWalletsProvider } from '@privy-io/react-auth/smart-wallets';
import { RouterProvider } from 'react-router-dom';
import { base, baseSepolia, foundry } from 'viem/chains';
import { router } from './router';
import { env, WidgetRegistryProvider } from '@rmint/shell';
import { widgetRegistry } from './components/widgets/registry';
import './styles/tailwind.css';

// The Privy SDK validates appId at init and throws if it is empty
// or malformed — so we only mount PrivyProvider when a real id is
// configured. In dev-bypass mode (no real Privy needed), the tree
// renders without the provider and `useAuth` returns a stub.

const root = ReactDOM.createRoot(document.getElementById('root')!);

// Privy's appearance.accentColor wants a literal hex (no CSS-var
// support in the SDK config). Read the design-system --primary token
// at runtime so the modal matches the brand without duplicating a
// hex literal in code (also keeps the audit's hex-literal rule happy).
// Returns undefined if the var is missing — Privy then uses its
// own default rather than us baking a fallback hex into source.
function readPrimaryColor(): `#${string}` | undefined {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary')
    .trim();
  return v.startsWith('#') ? (v as `#${string}`) : undefined;
}

const routerTree = <RouterProvider router={router} />;

// Pick the active chain from VITE_CHAIN_ENV. This drives Privy's wallet
// network so transactions originate on the right chain (otherwise Privy
// defaults to Ethereum mainnet and Subscribe Fund txs hang on the wrong
// network with the user prompt showing "Network: Ethereum").
const activeChain =
  env.chainEnv === 'base' ? base : env.chainEnv === 'local' ? foundry : baseSepolia;

const tree = env.privyAppId ? (
  <PrivyProvider
    appId={env.privyAppId}
    config={{
      loginMethods: ['email', 'wallet'],
      defaultChain: activeChain,
      supportedChains: [activeChain],
      appearance: {
        theme: 'light',
        accentColor: readPrimaryColor(),
      },
      embeddedWallets: {
        ethereum: { createOnLogin: 'users-without-wallets' },
      },
    }}
  >
    {/* Wraps the app in Privy's Smart Wallets context. The bundler +
        paymaster URLs come from the Privy dashboard's Smart Wallets
        configuration (Pimlico, set there). useSmartWallets() in
        src/lib/onchain.ts then drives ERC-4337 UserOps so
        Subscribe Fund txs are gas-sponsored — the user never needs
        Base Sepolia ETH. */}
    <SmartWalletsProvider>{routerTree}</SmartWalletsProvider>
  </PrivyProvider>
) : (
  routerTree
);

root.render(
  // Outermost wrapper: supplies the concrete widget registry to the
  // markdown renderer (which lives in @rmint/shell and reads it from
  // context). Wraps BOTH the Privy-present and Privy-absent trees.
  <WidgetRegistryProvider value={widgetRegistry}>
    <React.StrictMode>{tree}</React.StrictMode>
  </WidgetRegistryProvider>,
);
